import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowBack, Email, Business, CreditCard, CalendarToday, Person, CheckCircle, Cancel, Phone, Edit, ShoppingCart, Receipt } from "@mui/icons-material";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import { getUserProfile, getUserSubscription, updateUserSubscription } from "../../../services/adminService";

export default function UserProfileDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editForm, setEditForm] = useState({
    remainingCredits: '',
    planExpiry: ''
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const [userData, subscriptionResponse] = await Promise.all([
          getUserProfile(userId),
          getUserSubscription(userId)
        ]);
        setUser(userData || {});
        setSubscriptionData(subscriptionResponse || {});
      } catch (err) {
        console.error("Error loading user data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const getFullName = () => {
    if (!user) return "N/A";
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip
        icon={isActive ? <CheckCircle /> : <Cancel />}
        label={isActive ? "Active" : "Inactive"}
        color={isActive ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  const handleEditClick = (subscription) => {
    setSelectedSubscription(subscription);
    setEditForm({
      remainingCredits: subscription.remainingCredits.toString(),
      planExpiry: subscription.planExpiry ? subscription.planExpiry.split('T')[0] : ''
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedSubscription(null);
    setEditForm({ remainingCredits: '', planExpiry: '' });
  };

  const handleEditSave = async () => {
    try {
      setUpdating(true);
      
      // Use the adminService to update the subscription
      await updateUserSubscription(userId, selectedSubscription._id, {
        remainingCredits: parseInt(editForm.remainingCredits),
        planExpiry: editForm.planExpiry
      });

      // Close the dialog
      handleEditClose();
      
      // Refresh the subscription data
      const updatedSubscriptionData = await getUserSubscription(userId);
      setSubscriptionData(updatedSubscriptionData);
      
      // Also refresh user data to get updated credit information
      const updatedUserData = await getUserProfile(userId);
      setUser(updatedUserData);
      
    } catch (error) {
      console.error('Error updating subscription:', error);
      // You might want to show an error message to the user here
    } finally {
      setUpdating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <AdminDashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress size={60} sx={{ color: "#1BE885" }} />
        </Box>
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout>
      <Box sx={{ p: 3, width: "100%" }}>
        {/* Back Button */}
        <Box sx={{ mb: 3 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              color: "#1BE885",
              "&:hover": { color: "#16c973" },
              transition: "color 0.2s"
            }}
          >
            <ArrowBack fontSize="small" />
            <Typography variant="body2" fontWeight={500}>
              Back to Users
            </Typography>
          </Box>
        </Box>

        {/* User Profile Card */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.1)"
          }}
        >
          {/* Header Section */}
          

          {/* User Information - Full Width Layout */}
          <Box sx={{ width: "100%" }}>
            {/* Personal Information */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight={200} color="#333" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <Person sx={{ color: "#1BE885" }} />
                Personal Information
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <InfoRow label="First Name" value={user?.firstName || "N/A"} />
                <InfoRow label="Last Name" value={user?.lastName || "N/A"} />
                <InfoRow label="Email" value={user?.email || "N/A"} icon={<Email />} />
                <InfoRow label="Company" value={user?.company || "N/A"} icon={<Business />} />
                <InfoRow label="Phone" value={user?.phone || "N/A"} icon={<Phone />} />
              </Box>
            </Box>

            {/* Account & Subscription */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" fontWeight={200} color="#333" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard sx={{ color: "#1BE885" }} />
                Account & Subscription
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <InfoRow label="Total Credits" value={user?.totalCredits || "N/A"} />
                <InfoRow label="Remaining Credits" value={user?.remainingCredits || "N/A"} />
                <InfoRow label="Plan Expiry" value={formatDate(user?.planExpiry)} icon={<CalendarToday />} />
                {/* <InfoRow label="Account Status" value={user?.isActive ? "Active" : "Inactive"} /> */}
                <InfoRow label="Account Status" value={getStatusChip(user?.isActive)} />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Orders and Subscriptions Tabs */}
        <Paper elevation={3} sx={{ mt: 3, borderRadius: 3, bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.1)" }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} aria-label="orders and subscriptions tabs">
            <Tab label="Subscriptions" icon={<Receipt />} iconPosition="start" />
              <Tab label="Orders" icon={<ShoppingCart />} iconPosition="start" />
              
            </Tabs>
          </Box>

          {/* Orders Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 3 }}>
              {subscriptionData?.orders && subscriptionData.orders.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Payment Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptionData.orders.map((order) => (
                        <TableRow key={order._id}>
                          <TableCell>{order._id}</TableCell>
                          <TableCell>{order.planId}</TableCell>
                          <TableCell>{formatDate(order.createdAt)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={order.paymentData ? "Paid" : "Pending"} 
                              color={order.paymentData ? "success" : "warning"}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No orders found</Typography>
              )}
            </Box>
          )}

          {/* Subscriptions Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 3 }}>
              
              {subscriptionData?.subscription && subscriptionData.subscription.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan Type</TableCell>
                        <TableCell>Total Credits</TableCell>
                        <TableCell>Remaining Credits</TableCell>
                        <TableCell>Plan Expiry</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptionData.subscription
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .map((sub, index) => (
                        <TableRow key={sub._id}>
                          <TableCell>
                            <Chip 
                              label={sub.planType.replace('_', ' ')} 
                              color={sub.planType === 'free_plan' ? 'default' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{sub.totalCredits}</TableCell>
                          <TableCell>{sub.remainingCredits}</TableCell>
                          <TableCell>{formatDate(sub.planExpiry)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={sub.isActive ? "Active" : "Inactive"} 
                              color={sub.isActive ? "success" : "error"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(sub.createdAt)}</TableCell>
                          <TableCell>
                            {index === 0 && (
                              <IconButton 
                                size="small" 
                                color="primary"
                                title="Edit Latest Subscription"
                                onClick={() => handleEditClick(sub)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No subscriptions found</Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Edit Subscription Dialog */}
        <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            borderBottom: '1px solid #e0e0e0',
            // pb: 1
          }}>
            <Edit sx={{ color: '#1BE885' }} />
            Edit Subscription
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              <TextField
                label="Remaining Credits"
                type="number"
                value={editForm.remainingCredits}
                onChange={(e) => handleInputChange('remainingCredits', e.target.value)}
                fullWidth
                inputProps={{ min: 0, max: selectedSubscription?.totalCredits }}
                helperText={`Maximum: ${selectedSubscription?.totalCredits} credits`}
              />

              <TextField
                label="Plan Expiry Date"
                type="date"
                value={editForm.planExpiry}
                onChange={(e) => handleInputChange('planExpiry', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleEditClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave} 
              variant="contained" 
              disabled={updating}
              sx={{ 
                bgcolor: '#1BE885',
                '&:hover': { bgcolor: '#16c973' }
              }}
            >
              {updating ? 'Updating...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminDashboardLayout>
  );
}

function InfoRow({ label, value, icon }) {
  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "flex-start", 
      p: 2, 
      bgcolor: "#f8f9fa", 
      borderRadius: 2,
      minWidth: "100px",
      flex: "1 1 auto",
      border: "1px solid #e9ecef",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
    }}>
      <Box sx={{ display: "flex", alignItems: "center", }}>
        {icon && (
          <Box sx={{ color: "#1BE885", display: "flex", alignItems: "center" }}>
            {icon}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 200, fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
        {value}
      </Typography>
    </Box>
  );
}